import React, { useEffect, useMemo, useState, useRef, Suspense } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF, Float, Text3D, PerspectiveCamera, Stars, Html, useTexture, Environment } from '@react-three/drei';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Header } from './Header';
import { Footer } from './Footer';
import { CheckIcon, ShieldIcon, ClockIcon, AwardIcon, ArrowRightIcon, PlayCircleIcon, MapPinIcon, BadgeCheckIcon, ChevronRightIcon, UserIcon, GraduationCapIcon, BookOpenIcon } from 'lucide-react';
import * as THREE from 'three';
export const HACCPFoodSafetyPage3D = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const containerRef = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  });
  const handleScroll = () => {
    const scrollPosition = window.scrollY;
    const sections = document.querySelectorAll('section');
    sections.forEach(section => {
      const sectionTop = section.offsetTop - 100;
      const sectionHeight = section.offsetHeight;
      const sectionId = section.getAttribute('id');
      if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
        setActiveSection(sectionId);
      }
    });
  };
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return <div className="flex flex-col min-h-screen w-full bg-black text-white overflow-hidden" data-id="element-2051">
      <style jsx global data-id="element-2052">{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&display=swap');
        .orbitron {
          font-family: 'Orbitron', sans-serif;
        }
        .glow-text {
          text-shadow:
            0 0 10px rgba(0, 255, 255, 0.8),
            0 0 20px rgba(0, 255, 255, 0.4);
        }
        .gradient-text {
          background: linear-gradient(90deg, #00ffff, #0077ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .section-transition {
          position: relative;
          z-index: 10;
          height: 150px;
          margin-top: -75px;
          margin-bottom: -75px;
          pointer-events: none;
        }
        .noise-bg {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 250 250' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          opacity: 0.05;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }
      `}</style>
      {/* Single Canvas for entire page */}
      <div className="fixed inset-0 z-0" data-id="element-2053">
        <Canvas camera={{
        position: [0, 0, 10],
        fov: 45
      }} gl={{
        alpha: true
      }} dpr={[1, 2]} data-id="element-2054">
          <Suspense fallback={null} data-id="element-2055">
            <color attach="background" args={['#000']} data-id="element-2056" />
            <ambientLight intensity={0.2} data-id="element-2057" />
            <pointLight position={[10, 10, 10]} intensity={0.8} data-id="element-2058" />
            <pointLight position={[-10, -10, -10]} color="#00ffff" intensity={0.5} data-id="element-2059" />
            <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} data-id="element-2060" />
            <SceneController activeSection={activeSection} data-id="element-2061" />
          </Suspense>
        </Canvas>
      </div>
      <Header data-id="element-2062" />
      <main className="flex-grow relative z-10" ref={containerRef} data-id="element-2063">
        <HeroSection activeSection={activeSection} data-id="element-2064" />
        <SectionTransition color="#00ffff" data-id="element-2065" />
        <FeaturesSection activeSection={activeSection} data-id="element-2066" />
        <SectionTransition color="#0077ff" data-id="element-2067" />
        <CurriculumSection activeSection={activeSection} data-id="element-2068" />
        <SectionTransition color="#00ffff" data-id="element-2069" />
        <TestimonialsSection activeSection={activeSection} data-id="element-2070" />
        <SectionTransition color="#0077ff" data-id="element-2071" />
        <PricingSection activeSection={activeSection} data-id="element-2072" />
      </main>
      <Footer data-id="element-2073" />
      <NavigationIndicator activeSection={activeSection} data-id="element-2074" />
    </div>;
};
// New component to control what's displayed in the scene based on active section
const SceneController = ({
  activeSection
}) => {
  return <>
      {activeSection === 'hero' && <group key="hero-scene" data-id="element-2075">
          <FoodSafetyModel position={[0, 0, 0]} scale={1.5} data-id="element-2076" />
          <HeroParticles count={150} data-id="element-2077" />
        </group>}
      {activeSection === 'features' && <group key="features-scene" data-id="element-2078">
          <FeatureBackground data-id="element-2079" />
        </group>}
      {activeSection === 'curriculum' && <group key="curriculum-scene" data-id="element-2080">
          <CurriculumBackground data-id="element-2081" />
        </group>}
      {activeSection === 'testimonials' && <group key="testimonials-scene" data-id="element-2082">
          <TestimonialBackground data-id="element-2083" />
        </group>}
      {activeSection === 'pricing' && <group key="pricing-scene" data-id="element-2084">
          <PricingBackground data-id="element-2085" />
        </group>}
    </>;
};
const HeroSection = ({
  activeSection
}) => {
  const isActive = activeSection === 'hero';
  return <section id="hero" className="relative h-screen w-full overflow-hidden" data-id="element-2086">
      {/* Overlay with content */}
      <div className="absolute inset-0 z-10 flex flex-col justify-center items-center p-4" data-id="element-2087">
        <div className="container mx-auto px-4 md:px-6 relative" data-id="element-2088">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="max-w-3xl mx-auto text-center" data-id="element-2089">
            <motion.div className="inline-flex items-center px-4 py-2 rounded-full bg-cyan-500/20 backdrop-blur-sm border border-cyan-500/30 mb-6" initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.5
          }} data-id="element-2090">
              <MapPinIcon className="h-4 w-4 text-cyan-400 mr-2" data-id="element-2091" />
              <span className="text-white/90 text-sm font-medium" data-id="element-2092">
                FSAI Compliant Training for Ireland
              </span>
            </motion.div>
            <motion.h1 className="orbitron text-5xl md:text-6xl lg:text-7xl font-bold mb-6 glow-text" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 0.7
          }} data-id="element-2093">
              Level 1 Food Safety{' '}
              <span className="gradient-text" data-id="element-2094">Training</span>
            </motion.h1>
            <motion.p className="text-xl text-white/90 mb-8 leading-relaxed" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 0.9
          }} data-id="element-2095">
              The essential induction programme for all food handlers in
              Ireland. Fully aligned with FSAI requirements to protect consumers
              and meet your legal obligations.
            </motion.p>
            <motion.div className="flex flex-wrap justify-center gap-6 mb-8" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.1
          }} data-id="element-2096">
              <div className="flex items-center bg-white/10 backdrop-blur-sm px-4 py-3 rounded-lg border border-cyan-500/20" data-id="element-2097">
                <ClockIcon className="h-5 w-5 text-cyan-400 mr-3 flex-shrink-0" data-id="element-2098" />
                <span className="text-white" data-id="element-2099">Complete in 2 Hours</span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm px-4 py-3 rounded-lg border border-cyan-500/20" data-id="element-2100">
                <AwardIcon className="h-5 w-5 text-cyan-400 mr-3 flex-shrink-0" data-id="element-2101" />
                <span className="text-white" data-id="element-2102">FSAI Compliant</span>
              </div>
            </motion.div>
            <motion.div className="flex flex-col sm:flex-row justify-center gap-4 mb-8" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.3
          }} data-id="element-2103">
              <a href="#pricing" className="group px-6 py-3.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-lg transition-all duration-300 text-center shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 flex items-center justify-center" data-id="element-2104">
                <span data-id="element-2105">Enroll Now - Just €19</span>
                <ArrowRightIcon className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" data-id="element-2106" />
              </a>
              <a href="#curriculum" className="px-6 py-3.5 bg-transparent text-white font-medium rounded-lg border border-white/30 hover:bg-white/10 transition-all duration-300 text-center flex items-center justify-center group" data-id="element-2107">
                <PlayCircleIcon className="mr-2 h-5 w-5 text-cyan-400 group-hover:text-cyan-300" data-id="element-2108" />
                <span data-id="element-2109">Preview Course</span>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
      {/* Scroll indicator */}
      <motion.div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 2
    }} data-id="element-2110">
        <span className="text-white/60 text-sm mb-2 orbitron" data-id="element-2111">
          Scroll to explore
        </span>
        <div className="w-6 h-10 border-2 border-cyan-500/40 rounded-full flex items-start justify-center p-1" data-id="element-2112">
          <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce mt-1" data-id="element-2113"></div>
        </div>
      </motion.div>
      <div className="noise-bg" data-id="element-2114"></div>
    </section>;
};
const FeaturesSection = ({
  activeSection
}) => {
  const isActive = activeSection === 'features';
  const containerRef = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const features = [{
    icon: <ShieldIcon className="h-10 w-10" data-id="element-2115" />,
    title: 'FSAI Compliant',
    description: 'Fully aligned with Food Safety Authority of Ireland requirements'
  }, {
    icon: <ClockIcon className="h-10 w-10" data-id="element-2116" />,
    title: 'Time Efficient',
    description: 'Complete your certification in just 2 hours of focused learning'
  }, {
    icon: <AwardIcon className="h-10 w-10" data-id="element-2117" />,
    title: 'Instant Certification',
    description: 'Receive your certificate immediately upon successful completion'
  }, {
    icon: <BookOpenIcon className="h-10 w-10" data-id="element-2118" />,
    title: 'Interactive Learning',
    description: 'Engaging content with quizzes and real-world scenarios'
  }];
  return <section id="features" ref={containerRef} className="relative py-32 bg-black overflow-hidden" data-id="element-2119">
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2120">
        <motion.div className="max-w-3xl mx-auto text-center mb-16" initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-2121">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-500/20 backdrop-blur-sm mb-6" data-id="element-2122">
            <BadgeCheckIcon className="h-8 w-8 text-cyan-400" data-id="element-2123" />
          </div>
          <h2 className="orbitron text-4xl md:text-5xl font-bold mb-6 glow-text" data-id="element-2124">
            Next-Gen Food Safety Training
          </h2>
          <p className="text-xl text-white/80" data-id="element-2125">
            Discover why our cutting-edge approach to food safety training
            delivers superior results
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-6xl mx-auto" data-id="element-2126">
          {features.map((feature, index) => <motion.div key={index} className="bg-gradient-to-br from-black to-gray-900 border border-cyan-500/20 rounded-2xl p-8 backdrop-blur-md relative overflow-hidden group" initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-2127">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2128"></div>
              <div className="relative z-10" data-id="element-2129">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center mb-6 text-white" data-id="element-2130">
                  {feature.icon}
                </div>
                <h3 className="orbitron text-2xl font-bold mb-3 text-white" data-id="element-2131">
                  {feature.title}
                </h3>
                <p className="text-white/70" data-id="element-2132">{feature.description}</p>
                <div className="mt-6" data-id="element-2133">
                  <a href="#pricing" className="inline-flex items-center text-cyan-400 hover:text-cyan-300 transition-colors orbitron text-sm font-medium" data-id="element-2134">
                    Learn more
                    <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2135" />
                  </a>
                </div>
              </div>
              <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-500/20 to-blue-500/5 rounded-tl-full -mr-16 -mb-16 blur-2xl" data-id="element-2136"></div>
            </motion.div>)}
        </div>
      </div>
      <div className="noise-bg" data-id="element-2137"></div>
    </section>;
};
const CurriculumSection = ({
  activeSection
}) => {
  const isActive = activeSection === 'curriculum';
  const containerRef = useRef(null);
  const modules = [{
    number: '01',
    title: 'Introduction to Food Safety',
    duration: '20 mins',
    topics: ['Legal requirements', 'Food safety principles', 'Personal responsibilities']
  }, {
    number: '02',
    title: 'Food Contamination & Control',
    duration: '30 mins',
    topics: ['Biological hazards', 'Chemical hazards', 'Physical hazards']
  }, {
    number: '03',
    title: 'Personal Hygiene',
    duration: '25 mins',
    topics: ['Hand washing techniques', 'Proper work attire', 'Illness reporting']
  }, {
    number: '04',
    title: 'Cleaning & Disinfection',
    duration: '25 mins',
    topics: ['Cleaning schedules', 'Chemical safety', 'Verification procedures']
  }, {
    number: '05',
    title: 'Final Assessment',
    duration: '20 mins',
    topics: ['Comprehensive quiz', 'Certificate generation', 'Compliance verification']
  }];
  return <section id="curriculum" ref={containerRef} className="relative py-32 bg-gray-900 overflow-hidden" data-id="element-2138">
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2139">
        <motion.div className="max-w-3xl mx-auto text-center mb-16" initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-2140">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/20 backdrop-blur-sm mb-6" data-id="element-2141">
            <BookOpenIcon className="h-8 w-8 text-blue-400" data-id="element-2142" />
          </div>
          <h2 className="orbitron text-4xl md:text-5xl font-bold mb-6 glow-text" data-id="element-2143">
            Comprehensive Curriculum
          </h2>
          <p className="text-xl text-white/80" data-id="element-2144">
            Our structured learning path ensures complete coverage of essential
            food safety principles
          </p>
        </motion.div>
        <div className="max-w-5xl mx-auto" data-id="element-2145">
          {modules.map((module, index) => <motion.div key={index} className="mb-8 relative" initial={{
          opacity: 0,
          x: index % 2 === 0 ? -50 : 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-2146">
              <div className="bg-gradient-to-r from-gray-900 to-black border border-blue-500/20 rounded-2xl p-6 md:p-8 backdrop-blur-md relative overflow-hidden group" data-id="element-2147">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2148"></div>
                <div className="flex flex-col md:flex-row gap-6 items-start relative z-10" data-id="element-2149">
                  <div className="flex-shrink-0" data-id="element-2150">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white orbitron text-2xl font-bold" data-id="element-2151">
                      {module.number}
                    </div>
                  </div>
                  <div className="flex-grow" data-id="element-2152">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-4" data-id="element-2153">
                      <h3 className="orbitron text-2xl font-bold text-white mb-2 md:mb-0" data-id="element-2154">
                        {module.title}
                      </h3>
                      <div className="px-4 py-1 bg-blue-500/20 backdrop-blur-sm rounded-full text-blue-300 text-sm" data-id="element-2155">
                        {module.duration}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4" data-id="element-2156">
                      {module.topics.map((topic, i) => <div key={i} className="flex items-center" data-id="element-2157">
                          <div className="w-2 h-2 rounded-full bg-blue-400 mr-2" data-id="element-2158"></div>
                          <span className="text-white/80 text-sm" data-id="element-2159">{topic}</span>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
              {index < modules.length - 1 && <div className="absolute left-8 top-full h-8 w-0.5 bg-gradient-to-b from-blue-500 to-transparent" data-id="element-2160"></div>}
            </motion.div>)}
        </div>
        <motion.div className="text-center mt-16" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-2161">
          <a href="#pricing" className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium rounded-lg transition-all duration-300 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 orbitron" data-id="element-2162">
            Start Your Training Journey
            <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-2163" />
          </a>
        </motion.div>
      </div>
      <div className="noise-bg" data-id="element-2164"></div>
    </section>;
};
const TestimonialsSection = ({
  activeSection
}) => {
  const isActive = activeSection === 'testimonials';
  const testimonials = [{
    quote: "The 3D interactive elements made learning about food safety genuinely engaging. Best training I've ever taken!",
    name: "Sarah O'Connor",
    title: 'Restaurant Manager',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',
    rating: 5
  }, {
    quote: "Finally, a food safety course that doesn't put you to sleep! The immersive approach helped me retain information better.",
    name: 'Michael Byrne',
    title: 'Head Chef',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',
    rating: 5
  }, {
    quote: 'As a compliance officer, I appreciate how this course balances engaging content with regulatory requirements. Highly recommend!',
    name: 'Emma Murphy',
    title: 'Food Safety Auditor',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=761&q=80',
    rating: 5
  }];
  return <section id="testimonials" className="relative py-32 bg-black overflow-hidden" data-id="element-2165">
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2166">
        <motion.div className="max-w-3xl mx-auto text-center mb-16" initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-2167">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-500/20 backdrop-blur-sm mb-6" data-id="element-2168">
            <UserIcon className="h-8 w-8 text-cyan-400" data-id="element-2169" />
          </div>
          <h2 className="orbitron text-4xl md:text-5xl font-bold mb-6 glow-text" data-id="element-2170">
            Success Stories
          </h2>
          <p className="text-xl text-white/80" data-id="element-2171">
            Hear from food industry professionals who transformed their safety
            practices
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-2172">
          {testimonials.map((testimonial, index) => <motion.div key={index} className="bg-gradient-to-br from-black to-gray-900 border border-cyan-500/20 rounded-2xl p-8 backdrop-blur-md relative overflow-hidden group" initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-2173">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2174"></div>
              <div className="relative z-10" data-id="element-2175">
                <div className="flex mb-6" data-id="element-2176">
                  {[...Array(5)].map((_, i) => <svg key={i} className="w-5 h-5 text-cyan-400" fill="currentColor" viewBox="0 0 20 20" data-id="element-2177">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-2178" />
                    </svg>)}
                </div>
                <blockquote className="text-white/80 italic mb-6" data-id="element-2179">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center" data-id="element-2180">
                  <img src={testimonial.avatar} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover border-2 border-cyan-500/30 mr-4" data-id="element-2181" />
                  <div data-id="element-2182">
                    <h4 className="font-bold text-white" data-id="element-2183">{testimonial.name}</h4>
                    <p className="text-cyan-400 text-sm" data-id="element-2184">{testimonial.title}</p>
                  </div>
                </div>
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-cyan-500/20 to-blue-500/5 rounded-bl-full -mr-16 -mt-16 blur-2xl" data-id="element-2185"></div>
            </motion.div>)}
        </div>
      </div>
      <div className="noise-bg" data-id="element-2186"></div>
    </section>;
};
const PricingSection = ({
  activeSection
}) => {
  const isActive = activeSection === 'pricing';
  return <section id="pricing" className="relative py-32 bg-gray-900 overflow-hidden" data-id="element-2187">
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2188">
        <motion.div className="max-w-3xl mx-auto text-center mb-16" initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-2189">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/20 backdrop-blur-sm mb-6" data-id="element-2190">
            <GraduationCapIcon className="h-8 w-8 text-blue-400" data-id="element-2191" />
          </div>
          <h2 className="orbitron text-4xl md:text-5xl font-bold mb-6 glow-text" data-id="element-2192">
            Get Certified Today
          </h2>
          <p className="text-xl text-white/80" data-id="element-2193">
            Affordable training that delivers immediate value to your career and
            workplace
          </p>
        </motion.div>
        <div className="max-w-4xl mx-auto" data-id="element-2194">
          <motion.div className="bg-gradient-to-r from-gray-900 to-black border border-blue-500/30 rounded-2xl overflow-hidden backdrop-blur-md relative" initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-2195">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/5 opacity-50" data-id="element-2196"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 relative z-10" data-id="element-2197">
              <div className="p-8 md:p-12 border-b md:border-b-0 md:border-r border-blue-500/20" data-id="element-2198">
                <div className="inline-flex items-center px-3 py-1 bg-blue-500/90 text-white text-sm font-medium rounded-full mb-6" data-id="element-2199">
                  Most Popular
                </div>
                <h3 className="orbitron text-3xl font-bold mb-4 text-white" data-id="element-2200">
                  Individual Enrollment
                </h3>
                <div className="flex items-baseline mb-6" data-id="element-2201">
                  <span className="text-5xl font-bold text-white" data-id="element-2202">€19</span>
                  <span className="text-white/60 ml-2" data-id="element-2203">/ per person</span>
                </div>
                <p className="text-white/80 mb-8" data-id="element-2204">
                  Perfect for individual food handlers seeking certification
                </p>
                <ul className="space-y-4 mb-8" data-id="element-2205">
                  <li className="flex items-start" data-id="element-2206">
                    <CheckIcon className="h-6 w-6 text-blue-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2207" />
                    <span className="text-white/80" data-id="element-2208">
                      Instant access to full course
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2209">
                    <CheckIcon className="h-6 w-6 text-blue-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2210" />
                    <span className="text-white/80" data-id="element-2211">
                      Official FSAI compliant certificate
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2212">
                    <CheckIcon className="h-6 w-6 text-blue-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2213" />
                    <span className="text-white/80" data-id="element-2214">
                      12 months access to materials
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2215">
                    <CheckIcon className="h-6 w-6 text-blue-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2216" />
                    <span className="text-white/80" data-id="element-2217">
                      Mobile-friendly learning
                    </span>
                  </li>
                </ul>
                <a href="#individual-enrollment" className="block w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium rounded-lg transition-all duration-300 text-center shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 orbitron" data-id="element-2218">
                  Enroll Now
                </a>
              </div>
              <div className="p-8 md:p-12" data-id="element-2219">
                <div className="inline-flex items-center px-3 py-1 bg-cyan-500/90 text-white text-sm font-medium rounded-full mb-6" data-id="element-2220">
                  Best Value
                </div>
                <h3 className="orbitron text-3xl font-bold mb-4 text-white" data-id="element-2221">
                  Team Enrollment
                </h3>
                <div className="flex items-baseline mb-6" data-id="element-2222">
                  <span className="text-5xl font-bold text-white" data-id="element-2223">€15</span>
                  <span className="text-white/60 ml-2" data-id="element-2224">/ per person</span>
                </div>
                <p className="text-white/80 mb-8" data-id="element-2225">
                  Ideal for businesses training multiple staff members
                </p>
                <ul className="space-y-4 mb-8" data-id="element-2226">
                  <li className="flex items-start" data-id="element-2227">
                    <CheckIcon className="h-6 w-6 text-cyan-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2228" />
                    <span className="text-white/80" data-id="element-2229">
                      Everything in Individual plan
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2230">
                    <CheckIcon className="h-6 w-6 text-cyan-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2231" />
                    <span className="text-white/80" data-id="element-2232">
                      Bulk enrollment (10+ people)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2233">
                    <CheckIcon className="h-6 w-6 text-cyan-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2234" />
                    <span className="text-white/80" data-id="element-2235">
                      Team management dashboard
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2236">
                    <CheckIcon className="h-6 w-6 text-cyan-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-2237" />
                    <span className="text-white/80" data-id="element-2238">
                      Progress tracking & reporting
                    </span>
                  </li>
                </ul>
                <a href="#team-enrollment" className="block w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-lg transition-all duration-300 text-center shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 orbitron" data-id="element-2239">
                  Contact Sales
                </a>
              </div>
            </div>
          </motion.div>
          <motion.div className="mt-12 bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/30 rounded-xl p-6 backdrop-blur-md" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-2240">
            <div className="flex items-center" data-id="element-2241">
              <div className="w-12 h-12 bg-amber-500/30 backdrop-blur-sm rounded-full flex items-center justify-center mr-6 flex-shrink-0" data-id="element-2242">
                <ShieldIcon className="h-6 w-6 text-amber-400" data-id="element-2243" />
              </div>
              <div data-id="element-2244">
                <h3 className="orbitron font-bold text-xl text-white mb-1" data-id="element-2245">
                  100% Acceptance Guarantee
                </h3>
                <p className="text-white/80" data-id="element-2246">
                  If your certificate is not accepted by regulators, we'll
                  refund your purchase in full.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="noise-bg" data-id="element-2247"></div>
    </section>;
};
const SectionTransition = ({
  color
}) => {
  return <div className="section-transition" data-id="element-2248">
      <svg viewBox="0 0 1440 144" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" data-id="element-2249">
        <path d="M0 96L60 84C120 72 240 48 360 36C480 24 600 24 720 36C840 48 960 72 1080 84C1200 96 1320 96 1380 96L1440 96V144H1380C1320 144 1200 144 1080 144C960 144 840 144 720 144C600 144 480 144 360 144C240 144 120 144 60 144H0V96Z" fill={color} fillOpacity="0.1" data-id="element-2250" />
        <path d="M0 108L60 102C120 96 240 84 360 78C480 72 600 72 720 78C840 84 960 96 1080 102C1200 108 1320 108 1380 108L1440 108V144H1380C1320 144 1200 144 1080 144C960 144 840 144 720 144C600 144 480 144 360 144C240 144 120 144 60 144H0V108Z" fill={color} fillOpacity="0.2" data-id="element-2251" />
      </svg>
    </div>;
};
const NavigationIndicator = ({
  activeSection
}) => {
  const sections = [{
    id: 'hero',
    label: 'Home'
  }, {
    id: 'features',
    label: 'Features'
  }, {
    id: 'curriculum',
    label: 'Curriculum'
  }, {
    id: 'testimonials',
    label: 'Testimonials'
  }, {
    id: 'pricing',
    label: 'Pricing'
  }];
  return <div className="fixed right-6 top-1/2 transform -translate-y-1/2 z-50 hidden md:block" data-id="element-2252">
      <div className="flex flex-col items-center space-y-4" data-id="element-2253">
        {sections.map(section => <a key={section.id} href={`#${section.id}`} className="group flex items-center" data-id="element-2254">
            <div className={`w-3 h-3 rounded-full mr-2 transition-all duration-300 ${activeSection === section.id ? 'bg-cyan-400 shadow-glow' : 'bg-white/30'}`} style={{
          boxShadow: activeSection === section.id ? '0 0 10px rgba(0, 255, 255, 0.8)' : 'none'
        }} data-id="element-2255"></div>
            <span className={`text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${activeSection === section.id ? 'text-cyan-400' : 'text-white/70'}`} data-id="element-2256">
              {section.label}
            </span>
          </a>)}
      </div>
    </div>;
};
// 3D Models and Elements
const FoodSafetyModel = props => {
  const mesh = useRef();
  useFrame(state => {
    if (!mesh.current) return;
    mesh.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.3;
    mesh.current.rotation.x = Math.cos(state.clock.elapsedTime * 0.3) * 0.2;
    mesh.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.3;
  });
  return <group {...props} data-id="element-2257">
      <group ref={mesh} data-id="element-2258">
        <mesh data-id="element-2259">
          <torusKnotGeometry args={[1, 0.3, 128, 16]} data-id="element-2260" />
          <meshStandardMaterial color="#00ffff" emissive="#00ffff" emissiveIntensity={0.5} metalness={0.8} roughness={0.2} data-id="element-2261" />
        </mesh>
        <mesh position={[0, 0, 0]} data-id="element-2262">
          <sphereGeometry args={[0.5, 32, 32]} data-id="element-2263" />
          <meshStandardMaterial color="#0077ff" emissive="#0077ff" emissiveIntensity={0.3} transparent opacity={0.6} data-id="element-2264" />
        </mesh>
      </group>
    </group>;
};
const HeroParticles = ({
  count = 100
}) => {
  const groupRef = useRef();
  const particles = useMemo(() => {
    const newParticles = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        position: [(Math.random() - 0.5) * 20, (Math.random() - 0.5) * 20, (Math.random() - 0.5) * 20],
        scale: Math.random() * 0.2 + 0.1,
        speed: Math.random() * 0.01 + 0.005
      });
    }
    return newParticles;
  }, [count]);
  useFrame(state => {
    if (!groupRef.current) return;
    particles.forEach((particle, i) => {
      const child = groupRef.current.children[i];
      if (!child) return;
      const {
        speed,
        position
      } = particle;
      child.position.y = position[1] + Math.sin(state.clock.elapsedTime + i) * speed * 10;
      child.position.x = position[0] + Math.cos(state.clock.elapsedTime + i) * speed * 5;
    });
  });
  return <group ref={groupRef} data-id="element-2265">
      {particles.map((data, i) => <mesh key={i} position={data.position} data-id="element-2266">
          <sphereGeometry args={[data.scale, 16, 16]} data-id="element-2267" />
          <meshStandardMaterial color="#00ffff" emissive="#00ffff" emissiveIntensity={0.5} transparent opacity={0.7} data-id="element-2268" />
        </mesh>)}
    </group>;
};
const FeatureBackground = () => {
  const groupRef = useRef();
  useFrame(state => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y += 0.001;
    groupRef.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
  });
  return <group ref={groupRef} data-id="element-2269">
      <mesh position={[0, 0, -10]} data-id="element-2270">
        <torusGeometry args={[8, 0.5, 16, 100]} data-id="element-2271" />
        <meshStandardMaterial color="#00ffff" emissive="#00ffff" emissiveIntensity={0.2} wireframe transparent opacity={0.3} data-id="element-2272" />
      </mesh>
      <mesh position={[0, 0, -10]} rotation={[Math.PI / 2, 0, 0]} data-id="element-2273">
        <torusGeometry args={[12, 0.3, 16, 100]} data-id="element-2274" />
        <meshStandardMaterial color="#0077ff" emissive="#0077ff" emissiveIntensity={0.2} wireframe transparent opacity={0.2} data-id="element-2275" />
      </mesh>
    </group>;
};
const CurriculumBackground = () => {
  const groupRef = useRef();
  useFrame(state => {
    if (!groupRef.current) return;
    groupRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.3) * 0.1;
    groupRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.2) * 0.1;
  });
  return <group ref={groupRef} data-id="element-2276">
      <Stars radius={50} depth={50} count={1000} factor={2} saturation={0} fade speed={0.5} data-id="element-2277" />
      <mesh position={[0, 0, -15]} data-id="element-2278">
        <sphereGeometry args={[10, 32, 32]} data-id="element-2279" />
        <meshStandardMaterial color="#0077ff" emissive="#0077ff" emissiveIntensity={0.1} wireframe transparent opacity={0.1} data-id="element-2280" />
      </mesh>
    </group>;
};
const TestimonialBackground = () => {
  const groupRef = useRef();
  useFrame(state => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y += 0.001;
  });
  return <group ref={groupRef} data-id="element-2281">
      <Stars radius={50} depth={50} count={1000} factor={2} saturation={0} fade speed={0.5} data-id="element-2282" />
      <mesh position={[0, 0, -15]} data-id="element-2283">
        <octahedronGeometry args={[10, 0]} data-id="element-2284" />
        <meshStandardMaterial color="#00ffff" emissive="#00ffff" emissiveIntensity={0.1} wireframe transparent opacity={0.1} data-id="element-2285" />
      </mesh>
    </group>;
};
const PricingBackground = () => {
  const groupRef = useRef();
  useFrame(state => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y += 0.001;
    groupRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.2) * 0.05;
  });
  return <group ref={groupRef} data-id="element-2286">
      <Stars radius={50} depth={50} count={1000} factor={2} saturation={0} fade speed={0.5} data-id="element-2287" />
      <mesh position={[0, 0, -15]} data-id="element-2288">
        <icosahedronGeometry args={[10, 1]} data-id="element-2289" />
        <meshStandardMaterial color="#0077ff" emissive="#0077ff" emissiveIntensity={0.1} wireframe transparent opacity={0.1} data-id="element-2290" />
      </mesh>
    </group>;
};